import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Sparkles, BellRing, Rocket, ArrowRight, Loader2, Check } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { useT } from "../lib/i18n";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Progress } from "../components/ui/progress";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";

const COPY = {
  nl: {
    step: "Stap",
    of: "van",
    skip: "Overslaan",
    welcomeTitle: "Welkom bij WoningAlert, {name}! 👋",
    welcomeBody: "In drie korte stapjes zet je jouw zoekprofiel en eerste alert op. Zo ontvang je meteen de juiste woningen.",
    start: "Aan de slag",
    profileTitle: "Waar zoek je een woning?",
    profileBody: "Dit gebruiken we voor je persoonlijke matchscore.",
    alertTitle: "Zet je eerste alert aan",
    alertBody: "Ontvang direct bericht bij nieuwe passende woningen.",
    next: "Volgende",
    finishSave: "Alert aanzetten",
    doneTitle: "Je bent klaar! 🎉",
    doneBody: "We gaan direct voor je op zoek. Bekijk je matches op het dashboard.",
    toDash: "Naar mijn dashboard",
    browse: "Bekijk woningen",
    errCities: "Vul minstens één stad in.",
    savedProfile: "Zoekprofiel opgeslagen.",
    savedAlert: "Alert aangemaakt.",
  },
  en: {
    step: "Step",
    of: "of",
    skip: "Skip",
    welcomeTitle: "Welcome to WoningAlert, {name}! 👋",
    welcomeBody: "In three short steps you'll set up your search profile and first alert, so you instantly receive the right homes.",
    start: "Get started",
    profileTitle: "Where are you looking for a home?",
    profileBody: "We use this for your personal match score.",
    alertTitle: "Turn on your first alert",
    alertBody: "Get notified instantly about new matching homes.",
    next: "Next",
    finishSave: "Turn on alert",
    doneTitle: "You're all set! 🎉",
    doneBody: "We'll start searching for you right away. Check your matches on the dashboard.",
    toDash: "To my dashboard",
    browse: "Browse listings",
    errCities: "Enter at least one city.",
    savedProfile: "Search profile saved.",
    savedAlert: "Alert created.",
  },
};

const Welkom = () => {
  const { t, lang } = useT();
  const c = COPY[lang] || COPY.nl;
  const { user } = useAuth();
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({ cities: "", max_price: 2000, bedrooms: 1, frequency: "daily" });
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const cityList = () => form.cities.split(",").map((s) => s.trim()).filter(Boolean);

  const saveProfile = async () => {
    if (cityList().length === 0) return toast.error(c.errCities);
    setBusy(true);
    try {
      await api("/profile", {
        method: "POST",
        body: JSON.stringify({
          name: user?.name || "",
          cities: cityList(),
          max_price: Number(form.max_price),
          bedrooms: Number(form.bedrooms),
          min_m2: 0,
          household: "alleen",
          property_type: "appartement",
          furnished: "egaal",
          pets: false,
        }),
      });
      toast.success(c.savedProfile);
      setStep(2);
    } catch (e) { toast.error(e.message); } finally { setBusy(false); }
  };

  const saveAlert = async () => {
    setBusy(true);
    try {
      await api("/alerts", {
        method: "POST",
        body: JSON.stringify({
          city: cityList()[0] || form.cities.trim(),
          max_price: Number(form.max_price),
          bedrooms: Number(form.bedrooms),
          frequency: form.frequency,
        }),
      });
      toast.success(c.savedAlert);
      setStep(3);
    } catch (e) { toast.error(e.message); } finally { setBusy(false); }
  };

  const progress = ((step + 1) / 4) * 100;

  return (
    <div className="mx-auto flex min-h-[80vh] max-w-lg flex-col justify-center px-4 py-10">
      {step > 0 && step < 3 && (
        <div className="mb-6">
          <div className="mb-2 flex items-center justify-between text-xs font-semibold text-slate-400">
            <span>{c.step} {step} {c.of} 2</span>
            <button onClick={() => navigate("/dashboard")} className="hover:text-navy" data-testid="welkom-skip">{c.skip}</button>
          </div>
          <Progress value={progress} className="h-2" />
        </div>
      )}

      <div className="rounded-3xl border border-border bg-white p-8 shadow-sm">
        {step === 0 && (
          <div className="text-center" data-testid="welkom-intro">
            <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-3xl bg-brandblue-light text-brandblue">
              <Sparkles size={30} />
            </div>
            <h1 className="mt-6 font-display text-2xl font-extrabold text-navy">
              {c.welcomeTitle.replace("{name}", user?.name || "")}
            </h1>
            <p className="mt-3 text-slate-500">{c.welcomeBody}</p>
            <Button onClick={() => setStep(1)} data-testid="welkom-start" className="mt-8 w-full rounded-xl bg-brandblue py-6 text-base hover:bg-brandblue/90">
              {c.start} <ArrowRight size={18} className="ml-2" />
            </Button>
          </div>
        )}

        {step === 1 && (
          <div data-testid="welkom-profile">
            <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-brandblue text-white"><Sparkles size={22} /></div>
            <h2 className="mt-4 font-display text-xl font-bold text-navy">{c.profileTitle}</h2>
            <p className="mt-1 text-sm text-slate-500">{c.profileBody}</p>
            <div className="mt-6 space-y-4">
              <div>
                <Label>{t("p2.profiel.cities")}</Label>
                <Input className="mt-2 rounded-xl" data-testid="welkom-cities" value={form.cities} onChange={(e) => set("cities", e.target.value)} placeholder="Amsterdam, Utrecht" />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>{t("p2.profiel.maxrent")}</Label>
                  <Input className="mt-2 rounded-xl" type="number" value={form.max_price} onChange={(e) => set("max_price", e.target.value)} />
                </div>
                <div>
                  <Label>{t("detail.bedrooms")}</Label>
                  <Input className="mt-2 rounded-xl" type="number" value={form.bedrooms} onChange={(e) => set("bedrooms", e.target.value)} />
                </div>
              </div>
            </div>
            <Button onClick={saveProfile} disabled={busy} data-testid="welkom-profile-next" className="mt-8 w-full rounded-xl bg-brandblue py-6 text-base hover:bg-brandblue/90">
              {busy ? <Loader2 className="animate-spin" size={18} /> : <>{c.next} <ArrowRight size={18} className="ml-2" /></>}
            </Button>
          </div>
        )}

        {step === 2 && (
          <div data-testid="welkom-alert">
            <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-brandblue text-white"><BellRing size={22} /></div>
            <h2 className="mt-4 font-display text-xl font-bold text-navy">{c.alertTitle}</h2>
            <p className="mt-1 text-sm text-slate-500">{c.alertBody}</p>
            <div className="mt-6 space-y-4">
              <div>
                <Label>{t("p2.alerts.city")}</Label>
                <Input className="mt-2 rounded-xl" value={cityList()[0] || form.cities} onChange={(e) => set("cities", e.target.value)} data-testid="welkom-alert-city" />
              </div>
              <div>
                <Label>{t("p2.alerts.freq")}</Label>
                <Select value={form.frequency} onValueChange={(v) => set("frequency", v)}>
                  <SelectTrigger className="mt-2 rounded-xl"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="direct">{t("p2.freq.direct")}</SelectItem>
                    <SelectItem value="daily">{t("p2.freq.daily")}</SelectItem>
                    <SelectItem value="weekly">{t("p2.freq.weekly")}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <Button onClick={saveAlert} disabled={busy} data-testid="welkom-alert-save" className="mt-8 w-full rounded-xl bg-brandblue py-6 text-base hover:bg-brandblue/90">
              {busy ? <Loader2 className="animate-spin" size={18} /> : <><BellRing size={18} className="mr-2" /> {c.finishSave}</>}
            </Button>
          </div>
        )}

        {step === 3 && (
          <div className="text-center" data-testid="welkom-done">
            <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-3xl bg-success/15 text-success">
              <Check size={32} />
            </div>
            <h2 className="mt-6 font-display text-2xl font-extrabold text-navy">{c.doneTitle}</h2>
            <p className="mt-3 text-slate-500">{c.doneBody}</p>
            <Button onClick={() => navigate("/dashboard")} data-testid="welkom-to-dashboard" className="mt-8 w-full rounded-xl bg-brandblue py-6 text-base hover:bg-brandblue/90">
              <Rocket size={18} className="mr-2" /> {c.toDash}
            </Button>
            <Button variant="ghost" onClick={() => navigate("/woningen")} className="mt-2 w-full rounded-xl">
              {c.browse}
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

export default Welkom;
